import { createRoute } from "@tanstack/react-router";
import { Route as AdminLayoutRoute } from "./_layout";
import { FileX02 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useCardsToInvalidate } from "../../hooks/useMicrogroupQueries";
import { CardInvalidationCountdownBadge } from "../../components/admin/CardInvalidationCountdownBadge";
import type {
  CardInvalidationRow,
  CardInvalidationStatus,
} from "../../schemas/microgroup";

/**
 * UC-006 cards-to-invalidate queue (Phase 28 / Milestone 20.2).
 *
 * Per `spec/docs/04-architecture/admin-ui.md` §6. Data Table lists
 * `card_invalidation` rows s deadline countdown badge (`flagged_at + 48h`)
 * a SLA breach highlight. Application Shell critical Banner integrace je
 * v `_layout.tsx` (M2 final step).
 *
 * Tlačítko "Zneplatnit v Axigonu" je out-of-band akce — admin si otevře
 * Axigon klientskou zónu sám (per ANALYZE §2.6 — aplikace karty
 * neneplatňuje, jen označuje).
 */

export const Route = createRoute({
  getParentRoute: () => AdminLayoutRoute,
  path: "/admin/cards-to-invalidate",
  component: CardsToInvalidateComponent,
});

const STATUS_LABEL: Record<CardInvalidationStatus, string> = {
  flagged: "Čeká na zneplatnění",
  confirmed: "Zneplatněno v Axigonu",
};

const STATUS_COLOR: Record<CardInvalidationStatus, "warning" | "success" | "gray"> = {
  flagged: "warning",
  confirmed: "success",
};

function CardsToInvalidateComponent() {
  const query = useCardsToInvalidate();

  const columns: Column<CardInvalidationRow>[] = [
    {
      key: "fuel_card",
      header: "Karta",
      cell: (row) => (
        <div>
          <strong className="text-primary">{row.fuel_card_label ?? "—"}</strong>
          <div className="text-xs text-tertiary font-mono">
            {row.fuel_card_id}
          </div>
        </div>
      ),
    },
    {
      key: "member",
      header: "Bývalý člen",
      cell: (row) => row.member_display_name,
    },
    {
      key: "microgroup",
      header: "Mikroskupina",
      cell: (row) => row.microgroup_name,
    },
    {
      key: "flagged_at",
      header: "Označeno",
      cell: (row) =>
        new Date(row.flagged_at).toLocaleString("cs-CZ", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      key: "countdown",
      header: "Deadline (48 h)",
      cell: (row) =>
        row.status === "flagged" ? (
          <CardInvalidationCountdownBadge flaggedAt={row.flagged_at} />
        ) : (
          "—"
        ),
    },
    {
      key: "status",
      header: "Stav",
      cell: (row) => (
        <Badge color={STATUS_COLOR[row.status]}>
          {STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Akce",
      cell: (row) =>
        row.status === "flagged" ? (
          <Button
            size="sm"
            color="secondary"
            href="https://www.axigon.cz/klientska-zona"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`card-invalidate-axigon-link-${row.invalidation_id}`}
          >
            Otevřít v Axigonu
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <section aria-labelledby="cards-heading" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="cards-heading" className="text-display-xs font-bold text-primary m-0">
          Karty k invalidaci
        </h2>
        <p className="text-md text-tertiary m-0">
          Fronta karet s 48 h SLA na ruční zneplatnění v Axigon klientské zóně.
        </p>
      </header>

      <Card>
        <CardHeader title="Fronta karet" />
        <CardBody>
          {query.isLoading && <LoadingIndicator label="Načítám…" />}
          {query.isError && (
            <InlineAlert variant="error" title="Načítání selhalo">
              Frontu karet se nepodařilo načíst.
            </InlineAlert>
          )}
          {query.data && query.data.rows.length === 0 ? (
            <EmptyState size="sm">
              <EmptyState.Header>
                <EmptyState.FeaturedIcon color="gray" icon={FileX02} />
              </EmptyState.Header>
              <EmptyState.Content>
                <EmptyState.Title>Žádné karty nečekají na zneplatnění</EmptyState.Title>
                <EmptyState.Description>
                  Po rozpuštění mikroskupiny se zde objeví karty s 48h SLA.
                </EmptyState.Description>
              </EmptyState.Content>
            </EmptyState>
          ) : (
            query.data && (
              <DataTable
                caption="Karty čekající na zneplatnění v Axigon klientské zóně"
                columns={columns}
                rows={query.data.rows}
                rowKey={(r) => r.invalidation_id}
                emptyMessage="Žádné karty."
              />
            )
          )}
        </CardBody>
      </Card>
    </section>
  );
}
